import { useCallback } from 'react';
import { useMergeState } from '@/shared/lib/customHooks';
import type { DropdownInputProps } from './dropdownInput';

/**
 * Drives an open or closed dropdown input and reports the picked option to the form owner. Local merge state holds the open flag, a memoized toggle flips it, and selecting an item forwards the field name and option to the supplied change handler.
 *
 * @param name - Field name reported back on change.
 * @param onChange - Callback invoked with the field name and the selected option.
 * @returns An object exposing state, toggleDisplayDropdown and onClickDropdownItem.
 */
const useDropdownInput = ({ name, onChange }: Pick<DropdownInputProps, 'name' | 'onChange'>) => {
  const [state, setState] = useMergeState({
    isOpenDropdown: false,
  });

  /**
   * Flips the dropdown between open and closed. The merge-state setter writes the inverted open flag and the callback is memoized against that flag.
   *
   * @returns Nothing.
   */
  const toggleDisplayDropdown = useCallback(() => {
    setState({ isOpenDropdown: !state.isOpenDropdown });
  }, [state.isOpenDropdown]);

  /**
   * Reports the option a user picked from the dropdown list to the owning form. The field name and the chosen option are passed straight to the change handler without touching local state, so the dropdown stays open.
   *
   * @param option - The selected option value.
   * @returns Nothing.
   */
  const onClickDropdownItem = (option: string | number) => {
    onChange(name, option);
  };

  return { state, toggleDisplayDropdown, onClickDropdownItem };
};

export default useDropdownInput;
